/**
 * Catálogo de productos y servicios — lo que la empresa vende, con su precio.
 *
 * Antes el precio se escribía a mano en cada orden, cada vez de una forma
 * distinta, y no había manera de saber qué se vende más.
 *
 * El concepto de ingreso dice cómo se agrupa lo vendido en la contabilidad.
 * Se puede crear aquí mismo con solo el nombre: no hace falta saber de cuentas.
 *
 * Cambiar un precio aquí NO cambia las órdenes ya hechas: cada línea guardó el
 * precio con el que se vendió. Una orden de ayer no vale distinto porque hoy
 * suba la lista.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Stack, Alert, Tooltip,
  Switch, FormControlLabel, CircularProgress, InputAdornment,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../api';
import { useSede } from '../contexto/Sede';
import { useEsMovil, FILA, ancho, tablaAncha, ENCABEZADO, botonEncabezado, rotulo } from '../estilos/responsivo';

const fmt = (v) => '$' + new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(Number(v || 0));

const VACIO = { tipo: 'servicio', codigo: '', nombre: '', descripcion: '', precio: '', concepto_id: '',
                duracion_min: '', sede_id: '', orden: 0, activo: true };

export default function Servicios() {
  const esMovil = useEsMovil();
  const { sedes } = useSede();
  const [servicios, setServicios] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [edicion, setEdicion] = useState(null);
  const [errorForm, setErrorForm] = useState('');
  const [nuevoConcepto, setNuevoConcepto] = useState({ nombre: '', vende: 'servicios' });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, r] = await Promise.all([
        api.get('/servicios', { params: { incluir_inactivos: true } }),
        api.get('/conceptos', { params: { tipo: 'ingreso' } }),
        api.get('/servicios/mas-vendidos').catch(() => ({ data: [] })),
      ]);
      setServicios(s.data); setConceptos(c.data || []); setRanking(r.data || []);
      setError('');
    } catch (e) { setError(e.response?.data?.detail || 'No se pudo cargar el catálogo'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Un servicio nuevo arranca con el concepto de servicios que haya: casi
  // siempre es el correcto y evita dejarlo «sin asignar».
  const conceptoPorDefecto = (tipo = 'servicio') =>
    (conceptos.find(c => c.vende === (tipo === 'producto' ? 'productos' : 'servicios'))
      || conceptos[0] || {}).id || '';

  const abrirNuevo = () => {
    setEdicion({ ...VACIO, concepto_id: conceptoPorDefecto() });
    setNuevoConcepto({ nombre: '', vende: 'servicios' }); setErrorForm('');
  };

  // Al cambiar entre producto y servicio, si el concepto era el sugerido por
  // defecto, se cambia por el del otro tipo.
  const cambiarTipo = (tipo) => {
    if (!tipo) return;
    const sugerido = edicion.concepto_id === conceptoPorDefecto(edicion.tipo) || !edicion.concepto_id;
    setEdicion({ ...edicion, tipo,
      concepto_id: sugerido && !edicion.id ? conceptoPorDefecto(tipo) : edicion.concepto_id });
    setNuevoConcepto(n => ({ ...n, vende: tipo === 'producto' ? 'productos' : 'servicios' }));
  };

  const guardar = async () => {
    if (!edicion.nombre?.trim()) { setErrorForm('El nombre es obligatorio.'); return; }
    if (edicion.concepto_id === '__nuevo' && !nuevoConcepto.nombre.trim()) {
      setErrorForm('Escribe el nombre del concepto nuevo.'); return;
    }
    if (edicion.precio === '' || Number(edicion.precio) < 0) {
      setErrorForm('Pon un precio válido.'); return;
    }
    let conceptoId = edicion.concepto_id || null;
    if (conceptoId === '__nuevo') {
      try {
        const r = await api.post('/conceptos', { nombre: nuevoConcepto.nombre.trim(),
          tipo: 'ingreso', vende: nuevoConcepto.vende });
        conceptoId = r.data.id;
        setEdicion(e => ({ ...e, concepto_id: conceptoId }));
        setConceptos(cs => [...cs, r.data]);
      } catch (e) { setErrorForm(e.response?.data?.detail || 'No se pudo crear el concepto'); return; }
    }
    const cuerpo = { ...edicion,
      precio: Number(edicion.precio),
      concepto_id: conceptoId,
      sede_id: edicion.sede_id || null,
      duracion_min: edicion.duracion_min ? Number(edicion.duracion_min) : null,
      orden: Number(edicion.orden || 0) };
    try {
      if (edicion.id) await api.put(`/servicios/${edicion.id}`, cuerpo);
      else await api.post('/servicios', cuerpo);
      setEdicion(null); setErrorForm(''); cargar();
    } catch (e) { setErrorForm(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  const borrar = async (s) => {
    if (!window.confirm(`¿Borrar «${s.nombre}» del catálogo?`)) return;
    try { await api.delete(`/servicios/${s.id}`); cargar(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo borrar'); }
  };

  const masVendido = ranking[0];

  return (
    <Box>
      <Box sx={ENCABEZADO}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Lo que vendes y a qué precio. Al crear una orden se escoge de aquí.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          {...botonEncabezado(esMovil)}
          onClick={abrirNuevo}>
          {rotulo(esMovil, 'Nuevo', 'Nuevo ítem')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {masVendido && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Lo que más se vende: <b>{masVendido.nombre}</b> — {masVendido.cantidad} servicios
          por {fmt(masVendido.monto)}.
        </Alert>
      )}

      <Card>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" sx={tablaAncha(880)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Código', 'Nombre', 'Precio', 'Duración', 'Concepto',
                    'Sede', 'Vendido', 'Estado', ''].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11,
                      textTransform: 'uppercase', color: '#64748b' }}
                      align={h === 'Precio' ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {servicios.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>
                      {s.codigo || '—'}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                        {s.nombre}
                        <Chip size="small" label={s.tipo === 'producto' ? 'Producto' : 'Servicio'}
                          sx={{ ml: 1, height: 18, fontSize: 10,
                                bgcolor: s.tipo === 'producto' ? '#fef3c7' : '#e0f2fe',
                                color: s.tipo === 'producto' ? '#a16207' : '#0369a1' }} />
                      </Typography>
                      {s.descripcion && (
                        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                          {s.descripcion}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                      {fmt(s.precio)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>
                      {s.duracion_min ? `${s.duracion_min} min` : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {s.cuenta_codigo
                        ? <>{s.concepto_nombre}
                            <Typography sx={{ fontSize: 11, color: 'text.secondary',
                                              fontFamily: 'ui-monospace, monospace' }}>
                              {s.cuenta_codigo}
                            </Typography>
                          </>
                        : <Tooltip title="Sin concepto: al facturar no sabrá a qué cuenta de ingreso va">
                            <Chip size="small" label="sin asignar"
                              sx={{ height: 20, fontSize: 10, bgcolor: '#fef3c7', color: '#a16207' }} />
                          </Tooltip>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>
                      {s.sede_nombre || <span style={{ color: '#94a3b8' }}>Todas</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{s.veces_vendido}</TableCell>
                    <TableCell>
                      <Chip size="small" label={s.activo ? 'Activo' : 'Inactivo'}
                        color={s.activo ? 'success' : 'default'}
                        variant={s.activo ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small"
                          onClick={() => { setEdicion({ ...s, concepto_id: s.concepto_id || '',
                            sede_id: s.sede_id || '' }); setErrorForm('');
                            setNuevoConcepto({ nombre: '', vende: 'servicios' }); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Borrar">
                        <IconButton size="small" color="error" onClick={() => borrar(s)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {servicios.length === 0 && (
                  <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 5,
                    color: 'text.disabled' }}>
                    El catálogo está vacío. Crea tu primer producto o servicio.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog fullScreen={esMovil} open={Boolean(edicion)} onClose={() => setEdicion(null)} fullWidth maxWidth="sm">
        <DialogTitle>{edicion?.id ? `Editar ${edicion.tipo === 'producto' ? 'producto' : 'servicio'}`
          : 'Nuevo producto o servicio'}</DialogTitle>
        <DialogContent dividers>
          {errorForm && <Alert severity="error" sx={{ mb: 2 }}>{errorForm}</Alert>}
          {edicion && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <ToggleButtonGroup exclusive size="small" color="primary" fullWidth
                value={edicion.tipo || 'servicio'} onChange={(_, v) => cambiarTipo(v)}>
                <ToggleButton value="servicio">Servicio</ToggleButton>
                <ToggleButton value="producto">Producto</ToggleButton>
              </ToggleButtonGroup>
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Código" sx={ancho(130)}
                  value={edicion.codigo || ''}
                  onChange={e => setEdicion({ ...edicion, codigo: e.target.value.toUpperCase() })} />
                <TextField size="small" label="Nombre *" fullWidth value={edicion.nombre}
                  onChange={e => setEdicion({ ...edicion, nombre: e.target.value })} />
              </Stack>
              <TextField size="small" label="Descripción" fullWidth
                value={edicion.descripcion || ''}
                helperText="Qué incluye — se ve al escogerlo en la orden"
                onChange={e => setEdicion({ ...edicion, descripcion: e.target.value })} />
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Precio *" type="number" fullWidth
                  value={edicion.precio}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  onChange={e => setEdicion({ ...edicion, precio: e.target.value })} />
                {edicion.tipo !== 'producto' && (
                  <TextField size="small" label="Duración" type="number" fullWidth
                    value={edicion.duracion_min || ''}
                    InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                    onChange={e => setEdicion({ ...edicion, duracion_min: e.target.value })} />
                )}
              </Stack>
              <TextField select size="small" label="Concepto de ingreso" fullWidth
                value={edicion.concepto_id || ''}
                helperText="Cómo se agrupa en tus informes lo que vendas de esto"
                onChange={e => setEdicion({ ...edicion, concepto_id: e.target.value })}>
                {conceptos.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.icono ? `${c.icono}  ` : ''}{c.nombre}
                  </MenuItem>
                ))}
                <MenuItem value="__nuevo" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  + Crear un concepto nuevo
                </MenuItem>
                <MenuItem value="">Sin asignar</MenuItem>
              </TextField>
              {edicion.concepto_id === '__nuevo' && (
                <Stack direction={FILA} spacing={2} alignItems={{ sm: 'center' }}>
                  <TextField size="small" label="Nombre del concepto *" fullWidth autoFocus
                    value={nuevoConcepto.nombre}
                    placeholder="Ej.: Planes mensuales"
                    InputLabelProps={{ shrink: true }}
                    onChange={e => setNuevoConcepto({ ...nuevoConcepto, nombre: e.target.value })} />
                  <ToggleButtonGroup exclusive size="small" color="primary"
                    value={nuevoConcepto.vende}
                    onChange={(_, v) => v && setNuevoConcepto({ ...nuevoConcepto, vende: v })}>
                    <ToggleButton value="servicios">Servicios</ToggleButton>
                    <ToggleButton value="productos">Productos</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              )}
              <Stack direction={FILA} spacing={2}>
                <TextField select size="small" label="Sede" fullWidth
                  value={edicion.sede_id || ''}
                  helperText="Vacío = se ofrece en todas"
                  onChange={e => setEdicion({ ...edicion, sede_id: e.target.value })}>
                  <MenuItem value="">Todas las sedes</MenuItem>
                  {sedes.map(s => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Orden" type="number" sx={ancho(110)}
                  value={edicion.orden ?? 0}
                  onChange={e => setEdicion({ ...edicion, orden: e.target.value })} />
              </Stack>
              <FormControlLabel control={
                <Switch checked={Boolean(edicion.activo)}
                  onChange={e => setEdicion({ ...edicion, activo: e.target.checked })} />
              } label="Activo — se ofrece al vender" />
              {edicion.id && (
                <Alert severity="info" sx={{ fontSize: 12.5 }}>
                  Cambiar el precio no altera las órdenes ya hechas: cada una guardó
                  el precio con el que se vendió.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEdicion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
